import { PageHeader, SectionTitle } from "@/components/base/ui";

export default function About() {
  return (
    <div>
      <PageHeader
        title="About Us"
        subtitle="Stone of Distinction. Supplying premium natural stone to European architecture since day one."
      />

      {/* Our Story */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div>
              <span className="text-gold-500 text-xs font-sans uppercase tracking-[0.2em] mb-4 block">
                Our Story
              </span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 leading-tight mb-6">
                From Our Partner Quarries to the Heart of Europe
              </h2>
              <div className="space-y-4 text-stone-500 text-base md:text-lg font-sans leading-relaxed">
                <p>
                  Founded with a vision to bridge the gap between rich natural stone resources and Europe's growing demand for premium materials, Lithos has established itself as a trusted name in the B2B stone industry.
                </p>
                <p>
                  Our founders recognized that marble from our partner regions — particularly the renowned Blanc Imperial — possesses qualities that rival the world's most celebrated stones. With direct quarry partnerships and deep local expertise, we built a supply chain that delivers exceptional value.
                </p>
                <p>
                  Today, we serve architects, construction companies, stone fabricators, and distributors across France, Belgium, Switzerland, and beyond. Our commitment to quality, reliability, and transparent business practices has made us the preferred choice for professionals seeking premium natural stone.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Marble%20quarry%20workers%20extracting%20large%20stone%20blocks%2C%20professional%20documentary%20photography%2C%20warm%20natural%20lighting%2C%20earthy%20tones%2C%20industrial%20craftsmanship%2C%20authentic%20working%20scene%2C%20dramatic%20landscape%20background%2C%20high%20quality%2C%20respectful%20and%20dignified%20portrayal&width=600&height=500&seq=12&orientation=landscape"
                alt="Quarry workers"
                className="w-full h-80 md:h-96 object-cover rounded-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-lg p-5 shadow-sm border border-stone-100 max-w-xs">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-gold-50 rounded-lg">
                    <i className="ri-award-line text-gold-500 text-xl"></i>
                  </div>
                  <div>
                    <p className="font-serif text-lg font-semibold text-stone-900">
                      Premium Quality
                    </p>
                    <p className="text-stone-400 text-xs font-sans">
                      Direct quarry partnerships
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-16 md:py-24 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <SectionTitle
            label="Our Mission"
            title="Supplying Excellence, Building Trust"
            subtitle="We believe every architectural project deserves the finest natural stone. Our mission is to make premium marble from our partner regions accessible to European professionals."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-8">
            {[
              {
                icon: "ri-shield-check-line",
                title: "Trust & Transparency",
                text: "We believe in honest pricing, clear communication, and delivering exactly what we promise. Every slab is inspected and documented before shipping.",
              },
              {
                icon: "ri-ship-line",
                title: "Reliable Logistics",
                text: "Our container shipping network operates on fixed schedules. We handle customs, documentation, and freight forwarding so you can focus on your projects.",
              },
              {
                icon: "ri-hand-heart-line",
                title: "B2B Partnership",
                text: "We work exclusively with architects, builders, fabricators, and distributors. Volume pricing, flexible terms, and dedicated account management.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-stone-50 rounded-lg p-6 md:p-8 border border-stone-100"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-lg border border-stone-200 mb-5">
                  <i className={`${item.icon} text-gold-500 text-xl`}></i>
                </div>
                <h3 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-stone-500 text-sm font-sans leading-relaxed">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Logistics & Operations */}
      <section className="py-16 md:py-24 bg-stone-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-gold-500 text-xs font-sans uppercase tracking-[0.2em] mb-4 block">
                Our Operations
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900 leading-tight mb-6">
                Seamless Logistics from Quarry to Site
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: "Direct Quarry Sourcing",
                    text: "We maintain exclusive partnerships with premier quarries. This means first selection of the finest blocks and competitive pricing without intermediaries.",
                  },
                  {
                    title: "Container Shipping Experts",
                    text: "Regular FCL and LCL shipments from our partner ports to Marseille, Le Havre, and Barcelona. Full customs clearance and documentation handled by our team.",
                  },
                  {
                    title: "Quality Control",
                    text: "Every slab undergoes rigorous inspection before loading. We photograph, measure, and document each piece so you know exactly what you're receiving.",
                  },
                  {
                    title: "European Distribution",
                    text: "Our French warehouse enables just-in-time delivery for urgent projects. We also arrange direct transport to your fabrication facility or construction site.",
                  },
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-gold-500 text-white rounded-md text-sm font-sans font-bold flex-shrink-0 mt-1">
                      {idx + 1}
                    </div>
                    <div>
                      <h4 className="font-serif text-lg font-semibold text-stone-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-stone-500 text-sm font-sans leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src="https://readdy.ai/api/search-image?query=Cargo%20container%20ship%20at%20Mediterranean%20port%20during%20daytime%2C%20large%20container%20vessel%20docked%20at%20harbor%2C%20professional%20shipping%20logistics%20photography%2C%20industrial%20maritime%20scene%2C%20blue%20sky%20and%20warm%20sunlight%2C%20clean%20professional%20composition%2C%20import%20export%20business%20atmosphere&width=600&height=500&seq=13&orientation=landscape"
                alt="Container shipping"
                className="w-full h-80 md:h-96 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-stone-900">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { value: "500+", label: "Containers Shipped" },
              { value: "50+", label: "B2B Clients" },
              { value: "6", label: "Stone Varieties" },
              { value: "100%", label: "Quality Inspected" },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2">
                  {stat.value}
                </p>
                <p className="text-stone-400 text-sm font-sans uppercase tracking-wider">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}